import React, { useState, useRef, useEffect } from 'react';
import { Grid, Box, Paper, Typography, Button, CircularProgress } from '@mui/material';
import { api } from '../services/api';
import axios from 'axios';
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ReviewsIcon from '@mui/icons-material/Reviews';
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import {recordEvent} from "../utils/analytics-wrapper";
import {
  ThumbDown as ThumbDownFilledIcon, ThumbDownOutlined as ThumbDownIcon,
  ThumbUp as ThumbUpFilledIcon,
  ThumbUpOutlined as ThumbUpIcon
} from "@mui/icons-material";

const FormFields = ({ fileData, logger, anonGroup, reviewQuestionsData, location }) => {
  const [loading, setLoading] = useState({});
  const [results, setResults] = useState({});
  const [resultTypes, setResultTypes] = useState({});
  const [resultLengths, setResultLengths] = useState({});
  const [reviewIDs, setReviewIDs] = useState({});
  const [likedStates, setLikedStates] = React.useState({});
  const cancelSources = useRef({});

  useEffect(() => {
    // reset when fileData changes
    setLoading({});
    setResults({});
    setResultTypes({});
    setResultLengths({});
    setReviewIDs({});
    setLikedStates({});
    cancelSources.current = {};
  }, [fileData]);

  const conductAIReview = async (name, isGroup) => {
    // cancel any previous API call for this button
    if (cancelSources.current[name]) {
      cancelSources.current[name].cancel('Operation canceled by the user.');
    }

    // show that we're working and allow cancellation
    setLoading(prevLoading => ({ ...prevLoading, [name]: true }));
    const CancelToken = axios.CancelToken;
    cancelSources.current[name] = CancelToken.source();

    // organize questions for the API call
    let items;
    if (isGroup) {
      items = fileData.surveyItems.filter(item => item.groups.some(group => group.name === name));
    } else {
      items = fileData.surveyItems.filter(item => item.name === name);
    }
    const questions = items.map(item => {
      return {
        language: fileData.defaultLanguage,
        name: item.name,
        label: item.label || "",
        hint: item.hint || "",
        choices: item.choices ? item.choices.map(choice => ({
          label: choice.label || "",
          value: choice.value || ""
        })) : [],
        translations: item.translations ? item.translations.map(translation => ({
          language: translation.language,
          label: translation.label || "",
          hint: translation.hint || "",
          choices: translation.choices ? translation.choices.map(choice => ({
            label: choice.label || "",
            value: choice.value || ""
          })) : []
        })) : []
      };
    });

    // record analytics event
    recordEvent(
        {
        name: 'reviewQuestions',
        metrics: { numQuestions: questions.length },
        },
      logger,
    );

    let result;
    let resultType;
    let resultLength;
    let reviewID;
    try {
      const rawResult = await api.reviewQuestions(anonGroup, questions,
        reviewQuestionsData.surveyContext, reviewQuestionsData.surveyLocations, location,
        cancelSources.current[name].token);
      reviewID = rawResult.data.review_id;
      if (rawResult.data.error && rawResult.data.error.trim() !== '') {
        resultType = 'error';
        result = rawResult.data.error;
        resultLength = 0;
      }
      else {
        resultType = 'results';
        result = JSON.parse(rawResult.data.result);
        resultLength = rawResult.data.result.length;
      }
    } catch (error) {
      if (!axios.isCancel(error)) {
        console.error(error);
        reviewID = "";
        resultType = 'error';
        result = error.message;
        resultLength = error.message.length;
      }
    }

    if (resultType !== undefined) {
      setResultTypes(prevResultTypes => ({...prevResultTypes, [name]: resultType}));
      setResultLengths(prevResultLengths => ({...prevResultLengths, [name]: resultLength}));
      setResults(prevResults => ({...prevResults, [name]: result}));
      setReviewIDs(prevReviewIDs => ({...prevReviewIDs, [name]: reviewID}));
      setLikedStates(prevLikedStates => ({...prevLikedStates, [name]: 0}));
    }
    setLoading(prevLoading => ({...prevLoading, [name]: false}));
    cancelSources.current[name] = null;
  };

  const handleCancel = (name) => {
    if (cancelSources.current[name]) {
      cancelSources.current[name].cancel('Operation canceled by the user.');
      setLoading(prevLoading => ({ ...prevLoading, [name]: false }));
    }
  };

  const like = (name) => {
    if (likedStates[name] === 1) {
      api.restartLikeReview(reviewIDs[name], resultLengths[name], anonGroup);
      setLikedStates(prevLikedStates => ({...prevLikedStates, [name]: 0}));
  }
    else {
      api.likeReview(reviewIDs[name], resultLengths[name], anonGroup);
      setLikedStates(prevLikedStates => ({...prevLikedStates, [name]: 1}));
      recordEvent(
        {
          name: 'likeReview',
          metrics: { reviewLength: resultLengths[name] },
        },
        logger,
      );
    }
  };

  const dislike = (name) => {
    if (likedStates[name] === -1) {
      api.restartLikeReview(reviewIDs[name], resultLengths[name], anonGroup);
      setLikedStates(prevLikedStates => ({...prevLikedStates, [name]: 0}));
    }
    else {
      api.dislikeReview(reviewIDs[name], resultLengths[name], anonGroup);
      setLikedStates(prevLikedStates => ({...prevLikedStates, [name]: -1}));
      recordEvent(
        {
          name: 'dislikeReview',
          metrics: { reviewLength: resultLengths[name] },
        },
        logger,
      );
    }
  };

  const copyText = (textToCopy) => {
    recordEvent(
      {
        name: 'copyReviewResult',
        metrics: { resultLength: textToCopy.length },
      },
      logger,
    );
    navigator.clipboard.writeText(textToCopy);
  };

  const getImportanceColor = (importance) => {
    switch (importance) {
      case 5:
        return 'red';
      case 4:
        return '#CC5500';  // dark orange
      case 3:
        return 'black';
      case 2:
        return '#404040';  // darker grey
      case 1:
        return 'darkgrey';
      default:
        return 'black';
    }
  };

  const showResults = (result, resultType) => {
    return (
      <>
        {resultType === 'error' ? (
          <Box width="100%" style={{ display: 'flex', flexDirection: 'row', padding: '10px' }}>
            <ErrorOutlineIcon style={{color: 'red'}}/>
            <Typography variant="body1" style={{marginLeft: '10px'}}>
              {result}
            </Typography>
            <Tooltip title="Copy to clipboard">
                <span>
                  <IconButton aria-label="copy" onClick={() => copyText(result)}
                              style={{paddingTop: 0, paddingBottom: 0, paddingLeft: 10, paddingRight: 10}}>
                    <FileCopyIcon/>
                  </IconButton>
                </span>
            </Tooltip>
          </Box>
        ) : (
          <>
            {result.recommendations && result.recommendations.length === 0 && result.errors && result.errors.length === 0 ? (
              <Box width="100%" style={{ display: 'flex', flexDirection: 'row', padding: '10px' }}>
                <CheckCircleOutlineIcon style={{color: 'green', alignSelf: 'flex-start'}}/>
                <Box style={{marginLeft: '10px'}}>
                  <Typography variant="body1">
                    Well done! The AI didn&apos;t raise any concerns.
                  </Typography>
                </Box>
              </Box>
            ) : (
              <>
                {result.recommendations && result.recommendations.map((recommendation, index) => (
                  <Box width="100%" key={`recommendation-${index}`} style={{ display: 'flex', flexDirection: 'row', padding: '10px' }}>
                    <ReviewsIcon style={{color: 'green', alignSelf: 'flex-start'}}/>
                    <Box style={{marginLeft: '10px'}}>
                      <Typography variant="body1" style={{color: getImportanceColor(recommendation.importance)}}>
                        {recommendation.explanation} <p/>
                        <strong>Importance:</strong> {recommendation.importance} out of 5 <br/>
                        <strong>Evaluation type:</strong> {recommendation.evaluation_type}{' '}
                        <>
                          <Tooltip
                            title={<pre style={{whiteSpace: 'pre-wrap'}}>{recommendation.excerpt_evaluated}</pre>}>
                            <span>[ hover for evaluated text ]</span>
                          </Tooltip>
                        </>
                        <br/>
                        <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
                          <div>
                            <strong>Current text:</strong> {recommendation.replacement_original}
                          </div>
                          <Tooltip title="Copy to clipboard">
                            <span>
                              <IconButton aria-label="copy" onClick={() => copyText(recommendation.replacement_original)}
                                          style={{paddingTop: 0, paddingBottom: 0, paddingLeft: 10, paddingRight: 10}}>
                                <FileCopyIcon fontSize="small"/>
                              </IconButton>
                            </span>
                          </Tooltip>
                        </div>
                        <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
                          <div>
                            <strong>Replacement suggested:</strong> {recommendation.replacement_suggested}
                          </div>
                          <Tooltip title="Copy to clipboard">
                            <span>
                              <IconButton aria-label="copy" onClick={() => copyText(recommendation.replacement_suggested)}
                                          style={{paddingTop: 0, paddingBottom: 0, paddingLeft: 10, paddingRight: 10}}>
                                <FileCopyIcon fontSize="small"/>
                              </IconButton>
                            </span>
                          </Tooltip>
                        </div>
                      </Typography>
                    </Box>
                  </Box>
                ))}
                {result.errors && result.errors.map((error, index) => (
                  <Box width="100%" key={`error-${index}`}>
                    <ErrorOutlineIcon style={{color: 'red', alignSelf: 'flex-start'}}/>
                    <Box style={{marginLeft: '10px'}}>
                      <Typography variant="body1" style={{color: 'red'}}>
                        {error.error} <p/>
                        <Tooltip title="Copy to clipboard">
                          <span>
                            <IconButton aria-label="copy" onClick={() => copyText(error.error)}
                                        style={{paddingTop: 0, paddingBottom: 0, paddingLeft: 10, paddingRight: 10}}>
                              <FileCopyIcon fontSize="small"/>
                            </IconButton>
                          </span>
                        </Tooltip>
                        <strong>Evaluation type:</strong> {error.evaluation_type}{' '}
                        <>
                          <Tooltip title={<pre style={{whiteSpace: 'pre-wrap'}}>{error.excerpt_evaluated}</pre>}>
                            <span>[ hover for evaluated text ]</span>
                          </Tooltip>
                        </>
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </>
            )}
          </>
        )}
      </>
    );
  };

  const renderSurveyItems = (items) => {
    const shownGroups = [];
    return items.flatMap((item, index) => {
      let indent = 0;
      let groupCards = [];
      if (item.groups.length > 0) {
        groupCards = item.groups.map((group, groupIndex) => {
          if (!shownGroups.includes(group.name)) {
            shownGroups.push(group.name);
            indent = groupIndex * 20;
            return (
              <Box m={1} key={`group-${group.name}-${groupIndex}`} style={{ paddingLeft: `${indent}px` }}>
                <Paper elevation={2} style={{ padding: '1em' }}>
                  <Typography variant="h6" gutterBottom>
                    Group: {group.name} {group.isRepeated && "(repeated)"} {group.label && " - " + group.label}
                  </Typography>
                  <Box display="flex" justifyContent="flex-end">
                    {loading[group.name] ? (
                      <Button variant="contained" color="secondary" onClick={() => handleCancel(group.name)}>
                        Cancel
                      </Button>
                    ) : (
                      <Button variant="contained" color="primary" onClick={() => conductAIReview(group.name, true)}>
                        AI review
                      </Button>
                    )}
                    {loading[group.name] &&
                      <Box ml={2}>
                        <CircularProgress />
                      </Box>
                    }
                  </Box>
                  {results[group.name] && (
                    <Paper elevation={3} style={{padding: '0.25em 1em 1em 1em', marginTop: '1em'}}>
                      <Box mt={2} style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start'}}>
                        {showResults(results[group.name], resultTypes[group.name])}
                        <Box display="flex" justifyContent="center" width="100%">
                          <Tooltip title="Flag response as good or helpful">
                            <span>
                              <IconButton aria-label="like" onClick={() => like(group.name)} disabled={loading[group.name]}
                                          style={{ paddingTop: 0, paddingBottom: 0, paddingLeft: 10, paddingRight: 10 }}>
                                {likedStates[group.name] === 1 ? (
                                  <ThumbUpFilledIcon
                                    color={loading[group.name] ? 'rgba(0, 0, 0, 0.26)' : 'secondary'}
                                  />
                                ) : (
                                  <ThumbUpIcon color={loading[group.name] ? 'rgba(0, 0, 0, 0.26)' : 'secondary'} />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Flag response as bad or unhelpful">
                            <span>
                              <IconButton aria-label="dislike" onClick={() => dislike(group.name)} disabled={loading[group.name]}
                                          style={{ paddingTop: 0, paddingBottom: 0, paddingLeft: 10, paddingRight: 10 }}>
                                {likedStates[group.name] === -1 ? (
                                  <ThumbDownFilledIcon
                                    color={loading[group.name] ? 'rgba(0, 0, 0, 0.26)' : 'secondary'}
                                  />
                                ) : (
                                  <ThumbDownIcon
                                    color={loading[group.name] ? 'rgba(0, 0, 0, 0.26)' : 'secondary'}
                                  />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </Box>
                    </Paper>
                  )}
                </Paper>
              </Box>
            );
          }
          return null;
        }).filter(Boolean); // filter out null values
      }
      indent = item.groups.length * 20;
      const itemCard = (
        <Box m={1} key={`item-${index}`} style={{ paddingLeft: `${indent}px` }}>
          <Paper elevation={2} style={{padding: '1em'}}>
            <strong>Name: {item.name}</strong> <br/>
            <strong>Label:</strong> {item.label} <br/>
            <strong>Hint:</strong> {item.hint} <br/>
            <strong>Type:</strong> {item.type} <br/>
            {Array.isArray(item.choices) && (
              <>
                <strong>Choices:</strong>
                <ul>
                  {item.choices.map((choice, choiceIndex) => (
                    <li key={`choice-${item.name}-${choiceIndex}`}>
                      {choice.label} {choice.value !== choice.label && `- ${choice.value}`}
                    </li>
                  ))}
                </ul>
              </>
            )}
            {Array.isArray(item.translations) && item.translations.length > 0 && (
              <>
                <strong>Translations:</strong>
                <ul>
                  {item.translations.map((translation, translationIndex) => (
                    <li key={`translation-${item.name}-${translationIndex}`}>
                      {translation.language}
                    </li>
                  ))}
                </ul>
              </>
            )}
            <Box display="flex" justifyContent="flex-end">
              {loading[item.name] ? (
                <Button variant="contained" color="secondary" onClick={() => handleCancel(item.name)}>
                  Cancel
                </Button>
              ) : (
                <Button variant="contained" color="primary" onClick={() => conductAIReview(item.name, false)}>
                  AI review
                </Button>
              )}
              {loading[item.name] &&
                <Box ml={2}>
                  <CircularProgress />
                </Box>
              }
            </Box>
            {results[item.name] && (
              <Paper elevation={3} style={{padding: '0.25em 1em 1em 1em', marginTop: '1em'}}>
                <Box mt={2} style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start'}}>
                  {showResults(results[item.name], resultTypes[item.name])}
                  <Box display="flex" justifyContent="center" width="100%">
                    <Tooltip title="Flag response as good or helpful">
                      <span>
                        <IconButton aria-label="like" onClick={() => like(item.name)} disabled={loading[item.name]}
                                    style={{ paddingTop: 0, paddingBottom: 0, paddingLeft: 10, paddingRight: 10 }}>
                          {likedStates[item.name] === 1 ? (
                            <ThumbUpFilledIcon
                              color={loading[item.name] ? 'rgba(0, 0, 0, 0.26)' : 'secondary'}
                            />
                          ) : (
                            <ThumbUpIcon color={loading[item.name] ? 'rgba(0, 0, 0, 0.26)' : 'secondary'} />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Flag response as bad or unhelpful">
                      <span>
                        <IconButton aria-label="dislike" onClick={() => dislike(item.name)} disabled={loading[item.name]}
                                    style={{ paddingTop: 0, paddingBottom: 0, paddingLeft: 10, paddingRight: 10 }}>
                          {likedStates[item.name] === -1 ? (
                            <ThumbDownFilledIcon
                              color={loading[item.name] ? 'rgba(0, 0, 0, 0.26)' : 'secondary'}
                            />
                          ) : (
                            <ThumbDownIcon
                              color={loading[item.name] ? 'rgba(0, 0, 0, 0.26)' : 'secondary'}
                            />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>
              </Paper>
            )}
          </Paper>
        </Box>
      );
      return [...groupCards, itemCard];
    });
  };

  return (
    <Grid item xs={12}>
      {(fileData && Array.isArray(fileData.surveyItems)) ? (
        <Box m={1}>
          <Paper elevation={3} style={{ padding: '1em' }}>
            <Typography variant="h6" gutterBottom>
              Form fields
            </Typography>
            <Typography variant="body1">
              All form fields that have labels are listed here, and you can click to review any question or group. Knowing the fields in your form also helps the expression generator do a better job.  <p/>
            </Typography>
            {renderSurveyItems(fileData.surveyItems.filter(item => item.label))}
          </Paper>
        </Box>
      ) : (
        <Box m={1}>
          <Paper elevation={3} style={{ padding: '1em' }}>
            <Typography variant="body1">
              Please drop or select your XLSForm file at the top of the page to load it. That form will then
              be factored into your expressions and you&apos;ll be allowed to select questions to evaluate.
            </Typography>
          </Paper>
        </Box>
      )}
    </Grid>
  );
};

export default FormFields;
